import { useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useCountries, Country, CountryInsert } from "@/features/locations/hooks/useCountries";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

export default function Countries() {
  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;
  const { countries, isLoading, createCountry, updateCountry, deleteCountry, isCreating, isUpdating, isDeleting } = useCountries();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [formData, setFormData] = useState<CountryInsert>({ nome: "", sigla: null, codigoFone: null, codigoIbge: null, isAtivo: true });
  const [buscaPais, setBuscaPais] = useState("");

  const countriesFiltrados = buscaPais
    ? (countries ?? []).filter(c =>
        c.nome.toLowerCase().includes(buscaPais.toLowerCase()) ||
        (c.sigla ?? "").toLowerCase().includes(buscaPais.toLowerCase()))
    : (countries ?? []);

  const paginacao = usePaginacaoLocal(countriesFiltrados);

  const openNew = () => {
    setEditingCountry(null);
    setFormData({ nome: "", sigla: null, codigoFone: null, codigoIbge: null, isAtivo: true });
    setDialogOpen(true);
  };

  const openEdit = (country: Country) => {
    setEditingCountry(country);
    setFormData({ nome: country.nome, sigla: country.sigla, codigoFone: country.codigoFone, codigoIbge: country.codigoIbge, isAtivo: country.isAtivo });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (editingCountry) {
      updateCountry({ id: editingCountry.id, data: formData });
    } else {
      createCountry(formData);
    }
    setDialogOpen(false);
  };

  const handleDelete = () => {
    if (deleteId) {
      deleteCountry(deleteId);
      setDeleteId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Países</h1>
          <p className="text-muted-foreground">Gerencie os países cadastrados</p>
        </div>
        {podeEditar && (
          <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Novo País</Button>
        )}
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      <div className="max-w-sm">
        <Input
          placeholder="Buscar por nome ou sigla..."
          value={buscaPais}
          onChange={e => setBuscaPais(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Sigla</TableHead>
              <TableHead>DDI</TableHead>
              <TableHead>Cód. IBGE</TableHead>
              <TableHead className="text-center">Ativo</TableHead>
              <TableHead className="w-24">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center">Carregando...</TableCell></TableRow>
            ) : paginacao.itensPaginados.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">{buscaPais ? "Nenhum país encontrado para esta busca." : "Nenhum país cadastrado"}</TableCell></TableRow>
            ) : (
              paginacao.itensPaginados.map((country) => (
                <TableRow key={country.id}>
                  <TableCell className="font-medium">{country.nome}</TableCell>
                  <TableCell>{country.sigla || "-"}</TableCell>
                  <TableCell>{country.codigoFone || "-"}</TableCell>
                  <TableCell>{country.codigoIbge || "-"}</TableCell>
                  <TableCell className="text-center">{country.isAtivo ? "Sim" : "Não"}</TableCell>
                  <TableCell>
                    {podeEditar && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(country)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteId(country.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCountry ? "Editar País" : "Novo País"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Sigla</Label>
                <Input value={formData.sigla || ""} onChange={(e) => setFormData({ ...formData, sigla: e.target.value || null })} placeholder="BR" maxLength={3} />
              </div>
              <div className="space-y-2">
                <Label>DDI</Label>
                <Input value={formData.codigoFone || ""} onChange={(e) => setFormData({ ...formData, codigoFone: e.target.value || null })} placeholder="+55" />
              </div>
              <div className="space-y-2">
                <Label>Cód. IBGE</Label>
                <Input type="number" value={formData.codigoIbge || ""} onChange={(e) => setFormData({ ...formData, codigoIbge: e.target.value ? parseInt(e.target.value) : null })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.isAtivo ?? true} onCheckedChange={(checked) => setFormData({ ...formData, isAtivo: checked })} />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={!formData.nome || isCreating || isUpdating}>
              {isCreating || isUpdating ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja excluir este país? Países com estados vinculados não podem ser excluídos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
